/**
 * SupportBanner — persistent banner shown across all tabs when a support session is active.
 * Tapping navigates to the support chat screen.
 */

import React, { useEffect, useState } from 'react';
import { Text, StyleSheet, TouchableOpacity } from 'react-native';
import Animated, { useSharedValue, useAnimatedStyle, withTiming } from 'react-native-reanimated';
import { useSupportStore } from '../../stores/supportStore';
import { useRouter } from 'expo-router';
import { FONT_SIZE } from '../../utils/design-tokens';

export default function SupportBanner() {
  const session = useSupportStore((s) => s.session);
  const status = useSupportStore((s) => s.status);
  const operatorName = useSupportStore((s) => s.operatorName);
  const unreadCount = useSupportStore((s) => s.unreadCount);
  const loadActiveSession = useSupportStore((s) => s.loadActiveSession);
  const router = useRouter();
  const [hasSyncedSession, setHasSyncedSession] = useState(false);

  useEffect(() => {
    let isMounted = true;
    loadActiveSession()
      .catch(() => {
        // Best-effort sync; if it fails, keep the banner hidden rather than showing stale support state.
      })
      .finally(() => {
        if (isMounted) setHasSyncedSession(true);
      });

    return () => {
      isMounted = false;
    };
  }, [loadActiveSession]);

  const show = hasSyncedSession && !!session && (status === 'connecting' || status === 'active');
  const heightSV = useSharedValue(0);

  useEffect(() => {
    heightSV.value = withTiming(show ? 40 : 0, { duration: 250 });
  }, [show]);

  const animStyle = useAnimatedStyle(() => ({
    height: heightSV.value,
    opacity: heightSV.value > 0 ? 1 : 0,
  }));

  const label = status === 'connecting'
    ? '⏳ Waiting for support agent…'
    : `🎧 Chatting with ${operatorName || 'Support'}${unreadCount > 0 ? ` (${unreadCount} new)` : ''}`;

  return (
    <Animated.View style={[styles.banner, animStyle]}>
      <TouchableOpacity
        style={styles.touchable}
        onPress={() => router.push('/support-chat')}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel="Go to support chat"
      >
        <Text style={styles.text}>{label}</Text>
      </TouchableOpacity>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  banner: {
    backgroundColor: '#2196F3',
    overflow: 'hidden',
  },
  touchable: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  text: {
    color: '#fff',
    fontSize: FONT_SIZE.xs,
    fontWeight: '600',
    letterSpacing: 0.3,
  },
});
